import React from 'react';
import { useCatalogImportsQuery } from '@/api/queries/catalogImports';
import { useCatalogImport } from '@/pages/catalogImport/store';
import {
    DONE_IMPORTS_PAGE_SIZE,
    isUndoneImport,
} from '@/pages/onboarding/steps/done/doneImports';
import {
    linkableSteps,
    type OnboardingStep,
    type StepStripLinks,
    type StepStripMode,
} from './stepStripMeta';

export type UseStepStripLinksOptions = {
    mode: StepStripMode;
    currentStep: OnboardingStep | null;
    /** Accounting is a step of this visit (`useOnboardingPage`). */
    accountingNeeded: boolean;
    /** A step's address in this flow. */
    pathOf: (step: OnboardingStep) => string;
};

/**
 * The strip's links back (`linkableSteps`), each to its step's address.
 * Called inside a `CatalogImportProvider`; it reads the store:
 *
 * - **An import running** (`importStatus`) links nothing.
 * - **File busy:** on File, a file being read (`isReadingFile`) or checked
 *   (`previewStatus` running) links nothing, since Cancel is the step's only
 *   action. A check running on Names or Products locks nothing: going back
 *   abandons it, as their Back does.
 * - **Done** (wizard only) reads the organisation's import history, Done's
 *   own cached read (`useCatalogImportsQuery(null, 1, 200)`): Welcome is
 *   offered only while no import stands (an undone one does not count),
 *   and only once a read has answered so; never while one is on its way.
 */
export const useStepStripLinks = ({
    mode,
    currentStep,
    accountingNeeded,
    pathOf,
}: UseStepStripLinksOptions): StepStripLinks => {
    const { state } = useCatalogImport();
    const readsHistory = mode === 'wizard' && currentStep === 'done';
    const history = useCatalogImportsQuery(
        null,
        1,
        DONE_IMPORTS_PAGE_SIZE,
        readsHistory,
    );
    // Until a read answers (the first, a failed one, or the one an import
    // asked for), Welcome waits: it is never offered past an import.
    const hasCompletedImports =
        history.data === undefined ||
        history.isFetching ||
        history.data.items.some((item) => !isUndoneImport(item));
    const isFileBusy =
        currentStep === 'file' &&
        (state.isReadingFile || state.previewStatus === 'running');
    const isImporting = state.importStatus === 'running';

    return React.useMemo(() => {
        const steps = linkableSteps({
            mode,
            currentStep,
            accountingNeeded,
            isImporting,
            isFileBusy,
            hasCompletedImports,
        });
        return Object.fromEntries(
            [...steps].map((step) => [step, pathOf(step)]),
        );
    }, [
        accountingNeeded,
        currentStep,
        hasCompletedImports,
        isFileBusy,
        isImporting,
        mode,
        pathOf,
    ]);
};
